<script setup lang="ts">
import { formatFiltersAsQueries } from "./functions";
import { Filter } from "./types";

////////////////////////////////////////////////////////////
//////////////////// PROPS ///////////////////////////////
////////////////////////////////////////////////////////////
const props = defineProps<{
  showFilterBar: boolean;
  filters: Filter[];
}>();

const emit = defineEmits<{
  (e: "update:filters", value: typeof props.filters): void;
  (e: "update:showFilterBar", value: boolean): void;
  (e: "applyFilters", value: typeof props.filters): void;
  (e: "clearFilters"): void;
}>();
////////////////////////////////////////////////////////////
//////////////////// SETUP /////////////////////////////////
////////////////////////////////////////////////////////////
const router = useRouter();

////////////////////////////////////////////////////////////
//////////////////// COMPUTED ///////////////////////////////
////////////////////////////////////////////////////////////

const filters = computed({
  get: () => props.filters,
  set: (value) => emit("update:filters", value),
});

const showFilterBar = computed({
  get: () => props.showFilterBar,
  set: (value) => emit("update:showFilterBar", value),
});

////////////////////////////////////////////////////////////
//////////////////// METHODS ///////////////////////////////
////////////////////////////////////////////////////////////
function clearFilters() {
  // Clear filters
  filters.value.forEach((f) => {
    if (f.type === "range") f.value = [f.min, f.max];
    else if (f.multiple) f.value = [];
    else f.value = "";
  });

  // Emit clear
  emit("clearFilters");

  // Close filter bar
  showFilterBar.value = false;
}

function applyFilters() {
  // Emit filters
  emit("applyFilters", filters.value);

  // Add filters to the query
  const queries = formatFiltersAsQueries(filters.value);
  router.replace({
    query: {
      ...router.currentRoute.value.query,
      queries: JSON.stringify(queries),
    },
  });

  // Close filter bar
  showFilterBar.value = false;
}

function loadQueries(): void {
  // Load filters from query
  if (router.currentRoute.value.query.queries) {
    const queries = JSON.parse(
      router.currentRoute.value.query.queries as string,
    );
    filters.value.forEach((f) => {
      if (f.type === "range") {
        const entries = queries.filter((q: any) => q.field === f.key);
        if (entries.length === 2)
          f.value = [entries[0].value, entries[1].value];
        else f.value = [f.min, f.max];
      } else {
        const entry = queries.find((q: any) => q.field === f.key);
        if (entry) f.value = entry.value;
      }
    });
  }
}

////////////////////////////////////////////////////////////
//////////////////// HOOKS /////////////////////////////////
////////////////////////////////////////////////////////////
onMounted(() => {
  loadQueries();
});
</script>

<template>
  <VRow v-if="showFilterBar" class="mb-3">
    <VCol v-for="filter in filters" md="3">
      <FormInputSelect
        v-if="filter.type === 'select'"
        :config="{
          type: 'select',
          name: filter.key,
          label: filter.label ?? '',
          placeholder: filter.placeholder,
          multiple: filter.multiple,
          optionStore: filter.optionStore,
          options: filter.options,
          search: filter.search,
          valueMapping: filter.valueMapping,
          clearable: true,
          outlined: true,
        }"
        v-model="filter.value"
      />

      <FormInputAutoComplete
        v-else-if="filter.type === 'autocomplete'"
        :config="{
          type: 'autocomplete',
          name: filter.key,
          label: filter.label ?? '',
          placeholder: filter.placeholder,
          multiple: filter.multiple,
          optionStore: filter.optionStore,
          options: filter.options,
          search: filter.search,
          valueMapping: filter.valueMapping,
          clearable: true,
          outlined: true,
        }"
        v-model="filter.value"
      />

      <VRangeSlider
        class="mt-6"
        v-else-if="filter.type === 'range'"
        v-model="filter.value"
        :label="filter.label"
        :min="filter.min"
        :max="filter.max"
        :step="filter.step"
      />
    </VCol>
    <VCol cols="12">
      <VBtn @click="applyFilters" color="primary">
        <VIcon class="mr-2">tabler-search</VIcon>
        {{ $t("Apply filters") }}
      </VBtn>
      <VBtn class="ml-3" @click="clearFilters" color="default">
        <VIcon class="mr-2">tabler-filter-off</VIcon>
        {{ $t("Reset filters") }}
      </VBtn>
    </VCol>
  </VRow>
</template>
